import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowRight, Zap, Maximize, CheckCircle } from 'lucide-react';
import { getMachines } from '../services/apiService';
import { parseModels, powerRange, areaRange } from '../utils/machineModels';

const Machines = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('co2');
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const type = searchParams.get('type');
    if (type && ['co2', 'fiber', 'mixed'].includes(type)) {
      setActiveTab(type);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchMachines = async () => {
      try {
        setLoading(true);
        const data = await getMachines(activeTab);
        setMachines(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching machines:', error);
        setLoading(false);
      }
    };

    fetchMachines();
  }, [activeTab]);

  const tabs = [
    { id: 'co2', label: 'Laser CO2', description: 'Gravure & découpe non-métal' },
    { id: 'fiber', label: 'Laser Fibre', description: 'Découpe métal industrielle' },
    { id: 'mixed', label: 'Machines Mixtes', description: 'Technologie hybride CO2 + Fibre' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: '#5C3A21' }}></div>
          <p className="text-gray-600">Chargement des machines...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Nos Machines Laser
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Découvrez notre gamme complète de machines laser professionnelles.<br />
            Machines testées, validées et accompagnées d'une formation complète.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="sticky top-20 z-40 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 py-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 p-6 rounded-xl text-left transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'text-white shadow-xl transform scale-105'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                style={activeTab === tab.id ? { backgroundColor: '#5C3A21' } : {}}
              >
                <div className="text-lg font-bold mb-1">{tab.label}</div>
                <div className={`text-sm ${activeTab === tab.id ? 'text-amber-100' : 'text-gray-600'}`}>
                  {tab.description}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Machines Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {machines.map((machine) => (
              <div
                key={machine.id}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="aspect-[16/10] overflow-hidden bg-gray-50 flex items-center justify-center">
                  <img
                    src={machine.image}
                    alt={machine.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 p-2"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-4" style={{ color: '#5C3A21' }}>
                    {machine.name}
                  </h3>
                  <p className="text-gray-700 mb-6 leading-relaxed whitespace-pre-line">
                    {machine.description}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5EBDD' }}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Zap className="w-5 h-5" style={{ color: '#5C3A21' }} />
                        <span className="font-semibold text-sm" style={{ color: '#5C3A21' }}>Puissance</span>
                      </div>
                      <p className="text-gray-800 font-bold" data-testid={`machine-card-power-${machine.slug}`}>{powerRange(parseModels(machine.models), machine.power, ' – ')}</p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5EBDD' }}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Maximize className="w-5 h-5" style={{ color: '#5C3A21' }} />
                        <span className="font-semibold text-sm" style={{ color: '#5C3A21' }}>Zone de travail</span>
                      </div>
                      <p className="text-gray-800 font-bold" data-testid={`machine-card-area-${machine.slug}`}>{areaRange(parseModels(machine.models), machine.workArea, ' – ')}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold mb-3 text-gray-900">Matériaux compatibles :</h4>
                    <div className="flex flex-wrap gap-2">
                      {machine.materials.map((material, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-gray-800"
                        >
                          {material}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold mb-3 text-gray-900">Caractéristiques :</h4>
                    <ul className="space-y-2">
                      {machine.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#5C3A21' }} />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Prix</p>
                      <p className="text-2xl font-bold" style={{ color: '#5C3A21' }}>{machine.price}</p>
                    </div>
                    <Link
                      to={`/machines/${machine.slug}`}
                      className="inline-flex items-center px-6 py-3 rounded-lg text-white font-semibold shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                      style={{ backgroundColor: '#5C3A21' }}
                    >
                      Voir détails
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Access Occasions & Location */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/occasions"
              className="group relative overflow-hidden rounded-2xl border-2 border-amber-100 p-8 hover:shadow-2xl transition-all duration-300"
              style={{ backgroundColor: '#F5EBDD' }}
              data-testid="machines-link-occasions"
            >
              <h3 className="text-2xl font-bold mb-2" style={{ color: '#5C3A21' }}>Nos machines d'occasion</h3>
              <p className="text-gray-700 mb-4">Machines révisées, garanties et à prix réduit.</p>
              <span className="inline-flex items-center font-semibold" style={{ color: '#5C3A21' }}>
                Voir les occasions
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link
              to="/location"
              className="group relative overflow-hidden rounded-2xl border-2 border-amber-100 p-8 hover:shadow-2xl transition-all duration-300"
              style={{ backgroundColor: '#F5EBDD' }}
              data-testid="machines-link-location"
            >
              <h3 className="text-2xl font-bold mb-2" style={{ color: '#5C3A21' }}>Location de machines</h3>
              <p className="text-gray-700 mb-4">Sur place ou sur site, court ou long terme. Tarifs sur devis.</p>
              <span className="inline-flex items-center font-semibold" style={{ color: '#5C3A21' }}>
                Voir la location
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6" style={{ color: '#5C3A21' }}>
            Besoin d'aide pour choisir ?
          </h2>
          <p className="text-xl text-gray-700 mb-10 leading-relaxed">
            Nos experts vous conseillent pour sélectionner la machine adaptée à vos besoins,<br />
            votre budget et vos projets futurs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl text-white font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
              style={{ backgroundColor: '#5C3A21' }}
            >
              Demander un conseil
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white font-semibold border-2 hover:bg-gray-50 transition-all duration-300"
              style={{ borderColor: '#5C3A21', color: '#5C3A21' }}
            >
              Découvrir nos services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Machines;
